import { useQueries } from '@tanstack/react-query';
import { formatTimestamp } from '@topspin/shared';
import type { UsageRow } from '@topspin/shared';
import { fetchUsage } from '../api.js';
import type { SeasonView } from '../data.js';
import { groupsByScheduleStart } from '../session-utils.js';
import { getPlayerId } from '../storage.js';

/**
 * Admin-only usage report (§5, §9) — the web face of `npm run seen`: who has the app working,
 * how much they're using it, and who has never got in at all.
 *
 * One request per group rather than a field on `/bootstrap`, because this is the only screen
 * that wants it and every launch pays for that payload. The row ordering and the never-opened
 * rows come from the server, which derives them with the same shared function any report would.
 *
 * Cards go in schedule order, so Tuesday's group reads before Thursday's however the device
 * came to hold their group IDs. The queries are built from the same sorted list the cards are,
 * because they're paired by index.
 */
export default function UsageScreen({ view }: { view: SeasonView }) {
  const playerId = getPlayerId();
  // Only the groups the admin ID actually covers: a group reached through the players' ID
  // would 403, and a card that can only ever say "couldn't load" is worse than no card (§5).
  const groups = groupsByScheduleStart(view.groups.filter((g) => g.admin));
  const results = useQueries({
    queries: groups.map((group) => ({
      queryKey: ['usage', group.config.groupId, view.seasonId],
      queryFn: () =>
        fetchUsage(group.key, playerId, {
          seasonId: view.seasonId,
          groupId: group.config.groupId,
        }),
      // Visit counts move on every launch, so this is worth a fresh read on arrival — but not
      // one per re-render while you scroll a two-group list.
      staleTime: 30_000,
      retry: false,
    })),
  });

  return (
    <>
      <h1>Usage</h1>
      {groups.map((group, index) => {
        const result = results[index];
        return (
          <section className="card" key={group.config.groupId}>
            <h2>{group.config.name}</h2>
            {result?.data ? (
              <ul className="usage">
                {result.data.rows.map((row) => (
                  <UsageEntry key={row.playerId} row={row} />
                ))}
              </ul>
            ) : result?.isPending ? (
              <p className="muted">Loading…</p>
            ) : null}
            {/* Shown under whatever the cache still holds, so a failed refresh doesn't blank
                the page — it just says the numbers are the ones you last saw. */}
            {result?.error && (
              <p className="error">
                {result.data
                  ? "Couldn't refresh these numbers — they're the last ones loaded."
                  : "Couldn't load usage for this group."}
              </p>
            )}
          </section>
        );
      })}
    </>
  );
}

function UsageEntry({ row }: { row: UsageRow }) {
  // "never" rather than a dash: the absence is the finding, so it should read as a word (§5).
  const when = (at: string | undefined) => (at ? formatTimestamp(at) : 'never');
  return (
    <li>
      <p className="usage-name">
        {row.name}
        {!row.onRoster && <span className="muted"> — not on the roster</span>}
      </p>
      <dl className="usage-detail">
        <dt>first</dt>
        <dd>{when(row.firstSeenAt)}</dd>
        <dt>last</dt>
        <dd>{when(row.lastSeenAt)}</dd>
        <dt>visits</dt>
        <dd>{row.visits}</dd>
      </dl>
    </li>
  );
}
